import React, { useCallback, useMemo, useState } from "react";

import { AHText } from "arkh";
import {
  StoreAdminNavigationProp,
  StoreAdminNavigationScreenProps,
} from "../StoreAdmin/StoreAdminNavigation";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { IntegrationNewsfeedScreenQuery } from "./__generated__/IntegrationNewsfeedScreenQuery.graphql";

import { IntegrationLogo } from "./components/Logos/Logos";

import { FormattedMessage } from "react-intl";

import { getNewsfeedInfo } from "./IntegrationNewsfeedCreateScreen";

import { useAlert } from "../../common/Alert";
import { IntegrationNewsfeedScreenUpdateMutation } from "./__generated__/IntegrationNewsfeedScreenUpdateMutation.graphql";
import {
  IntegrationLogoItem,
  IntegrationTextItem,
} from "./components/IntegrationAddItem";
import { IntegrationSettings } from "./components/IntegrationSettings";

export function IntegrationNewsfeedScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationNewsfeed">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerIntegrationNewsfeedScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerIntegrationNewsfeedScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationNewsfeed"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<IntegrationNewsfeedScreenQuery>(
    graphql`
      query IntegrationNewsfeedScreenQuery($id: ID!) {
        store {
          integrationNewsfeed(id: $id) {
            id
            provider
          }
        }
      }
    `,
    {
      id: props.route.params.newsfeedId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const newsfeed = data.store.integrationNewsfeed!;

  const [value, setValue] = useState("");
  const providerInfo = useMemo(() => getNewsfeedInfo(value), [value]);

  const [update, updateLoading] =
    useMutation<IntegrationNewsfeedScreenUpdateMutation>(graphql`
      mutation IntegrationNewsfeedScreenUpdateMutation(
        $id: ID!
        $provider: IntegrationNewsfeedProvider!
        $info: String!
      ) {
        store {
          updateIntegrationNewsfeed(id: $id, provider: $provider, info: $info) {
            id
            provider
          }
        }
      }
    `);
  const Alert = useAlert();
  const onUpdate = useCallback(() => {
    update({
      onError: () => {
        Alert.alert(
          "Fel Uppstod",
          "Kunde inte skapa integrationen, kontrollera att du har knappat in rätt länk "
        );
      },
      onCompleted: (d) => {
        if (d.store.updateIntegrationNewsfeed?.id) {
          props.navigation.goBack();
        } else {
          Alert.error(
            "Fel Uppstod",
            "Kunde inte skapa integrationen, kontrollera att du har knappat in rätt länk "
          );
        }
      },
      variables: {
        id: newsfeed.id,
        provider: providerInfo!.provider,
        info: providerInfo!.info,
      },
    });
  }, [update, providerInfo, Alert, props.navigation, newsfeed.id]);

  const [deleteIntegration, deleteLoading] = useMutation(graphql`
    mutation IntegrationNewsfeedScreenDeleteMutation($id: ID!) {
      store {
        deleteIntegrationNewsfeed(id: $id)
      }
    }
  `);

  const onDelete = () => {
    deleteIntegration({
      variables: {
        id: props.route.params.newsfeedId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  };

  return (
    <IntegrationSettings
      delete={{
        pending: deleteLoading,
        requestDelete: onDelete,
      }}
      save={{
        disabled: !providerInfo,
        pending: updateLoading,
        requestSave: onUpdate,
      }}
      header={
        <IntegrationSettings.Header
          logo={
            newsfeed.provider === "FALCON" ? (
              <IntegrationLogo type="brandwatch" />
            ) : (
              <AHText type="title">
                <FormattedMessage
                  description="IntegrationsAdminNewsfeed create screen title"
                  defaultMessage="Newsfeed"
                />
              </AHText>
            )
          }
        />
      }
      body={
        <>
          <IntegrationSettings.Input
            value={value}
            onChangeText={setValue}
            type="url"
            placeholder="https://app.falcon.io/..."
            label={
              <FormattedMessage
                description="IntegrationNewsfeedScreen - Update - Title"
                defaultMessage="Update link"
              />
            }
          />
        </>
      }
    />
  );
}

export function IntegrationAdminNewsfeedListItem(props: {
  newsfeed: any;
  isFirst: boolean;
  isLast: boolean;
  navigation: StoreAdminNavigationProp<"IntegrationNewsfeed">;
}) {
  const data = useFragment(
    graphql`
      fragment IntegrationNewsfeedScreen_IntegrationNewsfeedListItem on IntegrationNewsfeed {
        id
        provider
      }
    `,
    props.newsfeed
  );

  if (data.provider === "FALCON") {
    return (
      <IntegrationLogoItem
        isFirst={props.isFirst}
        isLast={props.isLast}
        logoType="brandwatch"
        mode="positive"
        onPress={() => {
          props.navigation.navigate("IntegrationNewsfeed", {
            newsfeedId: data.id,
          });
        }}
      />
    );
  } else {
    return (
      <IntegrationTextItem
        isFirst={props.isFirst}
        isLast={props.isLast}
        mode="positive"
        onPress={() => {
          props.navigation.navigate("IntegrationNewsfeed", {
            newsfeedId: data.id,
          });
        }}
      >
        <AHText type="list-item-title">Newsfeed</AHText>
      </IntegrationTextItem>
    );
  }
}
